import React, { useState, useEffect } from "react";
import { Delete, Save, Settings, Info, Warning } from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import { useAuth } from "../../hooks/AuthProvider";
import {
  NODEBOT_NODE_TYPES,
  NODEBOT_NODES,
  STRIPE_LAST_WEBHOOK_EVENT_ENDPOINT,
  STRIPE_STATUS_ENDPOINT,
  TENANT_INTEGRATIONS,
  WOOCOMMERCE_STATUS_ENDPOINT,
} from "../../config/urls";
import { ArrayObjectField } from "./Components/ConfigFields/ArrayObjectField";
import { formatTitle, getNodeColor } from "./Components/utils";
import { EnumField } from "./Components/ConfigFields/EnumField";
import { NumberField } from "./Components/ConfigFields/NumberField";
import { BooleanField } from "./Components/ConfigFields/BooleanField";
import { StringField } from "./Components/ConfigFields/StringField";
import TextMessagePreset from "./Components/ConfigPresets/TextMessagePreset";
import SendEmailPreset from "./Components/ConfigPresets/SendEmailPreset";
import ScheduleTemplateMessagePreset from "./Components/ConfigPresets/ScheduleTemplateMessagePreset";
import TemplateFormMessagePreset from "./Components/ConfigPresets/TemplateFormMessagePreset";
import SendOtpPreset from "./Components/ConfigPresets/SendOtpPreset";
import SendWhatsAppFlowPreset from "./Components/ConfigPresets/SendWhatsAppFlowPreset";
import CompareDateOfBirthPreset from "./Components/ConfigPresets/CompareDateOfBirthPreset";
import CompareTagPreset from "./Components/ConfigPresets/CompareTagPreset";
import DeliveryMethodPreset from "./Components/ConfigPresets/DeliveryMethodPreset";
import ProductDiscoveryPreset from "./Components/ConfigPresets/ProductDiscoveryPreset";
import ScheduleTemplateBroadcastPreset from "./Components/ConfigPresets/ScheduleTemplateBroadcastPreset";
import LeadgenFormPreset from "./Components/ConfigPresets/LeadgenFormPreset";
import RichTextMessagePreset from "./Components/ConfigPresets/RichTextMessagePreset";
import InteractiveMenuPreset from "./Components/ConfigPresets/InteractiveMenuPreset";
import EmailNotificationPreset from "./Components/ConfigPresets/EmailNotificationPreset";
import WhatsAppNotificationPreset from "./Components/ConfigPresets/WhatsAppNotificationPreset";
import ShippingChargeCalculationPreset from "./Components/ConfigPresets/ShippingChargeCalculationPreset";
import SendSipReminderPreset from "./Components/ConfigPresets/SendSipReminderPreset";
import SendEmiReminderPreset from "./Components/ConfigPresets/SendEmiReminderPreset";
import SendLifeInsuranceReminderPreset from "./Components/ConfigPresets/SendLifeInsuranceReminderPreset";
import SendHealthInsuranceReminderPreset from "./Components/ConfigPresets/SendHealthInsuranceReminderPreset";
import SendGeneralInsuranceReminderPreset from "./Components/ConfigPresets/SendGeneralInsuranceReminderPreset";
import PaymentLinkPreset from "./Components/ConfigPresets/PaymentLinkPreset";
import OrderConfirmationPreset from "./Components/ConfigPresets/OrderConfirmationPreset";
import SurveyQuestionsPreset from "./Components/ConfigPresets/SurveyQuestionsPreset";
import LiveAgentPreset, {
  validateLiveAgentConfig,
} from "./Components/ConfigPresets/LiveAgentPreset";
import { OtpPanelHeader } from "./Components/ConfigPresets/OtpPanelParts";
import OtpConnectionPreset from "./Components/ConfigPresets/OtpConnectionPreset";
import OtpSelectTemplatePreset from "./Components/ConfigPresets/OtpSelectTemplatePreset";
import OtpSendPreset from "./Components/ConfigPresets/OtpSendPreset";
import OtpVerifyPreset from "./Components/ConfigPresets/OtpVerifyPreset";
import OtpResendPreset from "./Components/ConfigPresets/OtpResendPreset";
import EndNodePreset from "./Components/ConfigPresets/EndNodePreset";

const NodeConfig = ({ selectedNode, onUpdate, onDelete, tags, templates, nodes }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [config, setConfig] = useState({});
  const [savedConfig, setSavedConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nodeType, setNodeType] = useState(null);
  const [nodeTypes, setNodeTypes] = useState(null);

  const isDirty = JSON.stringify(config) !== JSON.stringify(savedConfig);

  // Warn on reload/close (not in-app navigation) so an uploaded MediaAsset
  // never gets silently orphaned by an unsaved config change being discarded.
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const [enabledPaymentGateways, setEnabledPaymentGateways] = useState([]);
  const [paymentGatewayDetails, setPaymentGatewayDetails] = useState({});
  const [lastStripeWebhookEvent, setLastStripeWebhookEvent] = useState(null);
  const [enabledEcommercePlatforms, setEnabledEcommercePlatforms] = useState([]);
  const [ecommercePlatformDetails, setEcommercePlatformDetails] = useState({});

  useEffect(() => {
    loadNodeTypes();
    getPaymentGateways();
    getLastStripeWebhookEvent();
    getEcommercePlatforms();
  }, []);

  const getEcommercePlatforms = async () => {
    // Shopify/Magento have no connect flow yet — they're listed in the
    // dropdown (OrderConfirmationPreset) but never included here, so they
    // always render "Not connected"/disabled until that work exists.
    const token = await getValidToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(WOOCOMMERCE_STATUS_ENDPOINT, { headers });

      if (!response.ok) {
        console.error("Failed to fetch WooCommerce status");
        return;
      }

      const data = await response.json();
      setEnabledEcommercePlatforms(data.connected ? ["woocommerce"] : []);
      setEcommercePlatformDetails(
        data.connected ? { woocommerce: { siteUrl: data.site_url } } : {},
      );
    } catch (error) {
      console.error("Failed to fetch WooCommerce status", error);
    }
  };

  const getLastStripeWebhookEvent = async () => {
    const token = await getValidToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(STRIPE_LAST_WEBHOOK_EVENT_ENDPOINT, { headers });

      if (!response.ok) {
        console.error("Failed to fetch last Stripe webhook event");
        return;
      }

      const data = await response.json();
      setLastStripeWebhookEvent(data.event || null);
    } catch (error) {
      console.error("Failed to fetch last Stripe webhook event", error);
    }
  };

  const loadNodeTypes = async (key) => {
    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(`${NODEBOT_NODE_TYPES}?limit=999`, { headers });

      if (!response.ok) throw new Error("Failed to load node types");

      const types = (await response.json()).results;
      setNodeTypes(types);
    } catch (err) {
      console.error("Failed to load node type:", err);
    }
  };

  useEffect(() => {
    if (!selectedNode || !nodeTypes) return;

    setConfig(selectedNode?.data?.config || {});
    setSavedConfig(selectedNode?.data?.config || {});

    const type = nodeTypes.find(
      (t) => t.key === selectedNode.data.node_type_key,
    );

    setNodeType(type || null);
  }, [selectedNode, nodeTypes]);

  const updateNode = async (nodeId, config) => {
    const token = await getValidToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    const body = {
      config,
    };

    const response = await fetch(`${NODEBOT_NODES}${nodeId}/`, {
      headers,
      method: "PATCH",
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Failed to save configuration");
    }

    return data;
  };

  const deleteNode = async (nodeId) => {
    const token = await getValidToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    const response = await fetch(`${NODEBOT_NODES}/${nodeId}/delete/`, {
      headers,
      method: "DELETE",
    });

    const data = await response.json();

    return data;
  };

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateConfig = () => {
    console.log("Validating config:", config, nodeType, nodeType?.config_schema?.required);
    if (!nodeType?.config_schema?.required) return true;

    for (const field of nodeType.config_schema.required) {
      const value = config[field];

      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        setError(`${formatTitle(field)} is required.`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!selectedNode) return;
    if (!validateConfig()) return;

    if (
      selectedNode.data?.node_type_key === "live_agent_toggle" &&
      Object.keys(validateLiveAgentConfig(config)).length > 0
    ) {
      setError("Fix the highlighted Live Agent fields before saving");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await updateNode(selectedNode?.data?.id, config);

      if (!response) throw new Error("Failed to save configuration");

      setSavedConfig(config);
      onUpdate(selectedNode?.data?.id, config);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNode) return;

    if (selectedNode.data?.category === "start") {
      setError("Cannot delete the start node");
      return;
    }

    if (window.confirm("Are you sure you want to delete this node?")) {
      setLoading(true);
      setError(null);

      try {
        await deleteNode(selectedNode?.data?.id);
        onDelete(selectedNode?.data?.id);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to delete node");
      } finally {
        setLoading(false);
      }
    }
  };

  const getPaymentGateways = async () => {
    const possible_payment_gateways = ["cashfree", "razorpay", "phonepe"];

    const token = await getValidToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    let legacyEnabledGateways = [];
    try {
      const response = await fetch(TENANT_INTEGRATIONS, { headers });

      if (!response.ok) {
        console.error("Failed to fetch integrations");
      } else {
        const data = await response.json();
        legacyEnabledGateways = data.integrations.filter((integration) =>
          possible_payment_gateways.includes(integration),
        );
      }
    } catch (error) {
      console.error("Failed to fetch integrations", error);
    }

    // Stripe is stored via the dedicated integrations app
    // (TenantIntegrationConnection), not the legacy Tenant.integrations
    // JSONField the other gateways above use — so its connected state is
    // checked separately, mirroring IntegrationsOverview.js.
    let stripeConnected = false;
    let stripeDetails = null;
    try {
      const response = await fetch(STRIPE_STATUS_ENDPOINT, { headers });

      if (!response.ok) {
        console.error("Failed to fetch Stripe status");
      } else {
        const data = await response.json();
        stripeConnected = !!data.connected;
        if (stripeConnected) {
          stripeDetails = {
            accountId: data.account_id,
            mode: data.mode,
            webhookUrl: data.webhook_url,
            webhookRegistered: !!data.webhook_registered,
            webhookRegistrationError: data.webhook_registration_error || "",
          };
        }
      }
    } catch (error) {
      console.error("Failed to fetch Stripe status", error);
    }

    setEnabledPaymentGateways([
      ...legacyEnabledGateways,
      ...(stripeConnected ? ["stripe"] : []),
    ]);
    setPaymentGatewayDetails({
      ...(stripeDetails ? { stripe: stripeDetails } : {}),
    });
  };

  if (!selectedNode) {
    return (
      <Stack
        sx={{
          flex: 0.25,
          borderLeft: "1px solid",
          borderColor: ColorsConstants.primaryGray10,
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Info
          sx={{ fontSize: 48, color: ColorsConstants.primaryGray40, mb: 2 }}
        />
        <Typography
          sx={{
            ...typography.bodyLarge,
            color: ColorsConstants.primaryGray60,
            textAlign: "center",
          }}
        >
          Select a node to configure its settings
        </Typography>
      </Stack>
    );
  }

  const renderConfigFields = () => {
    if (!nodeType) return null;
    if (nodeType.category === "start") return null;

    // The end node's config is optional (closing message), so it renders even for older end nodes
    if (nodeType.category === "end") {
      return (
        <EndNodePreset config={config} handleConfigChange={handleConfigChange} />
      );
    }

    const schema = nodeType?.config_schema;
    if (!schema?.properties) return null;

    const properties = schema.properties;

    // Determine render order
    const HIDDEN_FIELDS = [];
    const orderedKeys = (schema.field_order || Object.keys(properties)).filter(
      (key) => !HIDDEN_FIELDS.includes(key),
    );

    console.log("Selected Node Type: ", selectedNode?.data?.node_type)

    if (selectedNode?.data?.node_type_key === "live_agent_toggle") {
      return (
        <LiveAgentPreset
          key={selectedNode?.data?.id}
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    const otpPresets = {
      otp_connection: OtpConnectionPreset,
      otp_select_template: OtpSelectTemplatePreset,
      otp_send: OtpSendPreset,
      otp_verify: OtpVerifyPreset,
      otp_resend: OtpResendPreset,
    };
    const OtpPreset = otpPresets[selectedNode?.data?.node_type_key];
    if (OtpPreset) {
      return (
        <OtpPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
          nodes={nodes}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "text_message") {
      return (
        <TextMessagePreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_email") {
      return (
        <SendEmailPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_sip_reminder") {
      return (
        <SendSipReminderPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_emi_reminder") {
      return (
        <SendEmiReminderPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_life_insurance_reminder") {
      return (
        <SendLifeInsuranceReminderPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_health_insurance_reminder") {
      return (
        <SendHealthInsuranceReminderPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_general_insurance_reminder") {
      return (
        <SendGeneralInsuranceReminderPreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_whatsapp_flow") {
      return (
        <SendWhatsAppFlowPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "send_otp") {
      return (
        <SendOtpPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "template_form_message") {
      return (
        <TemplateFormMessagePreset
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "schedule_template_message") {
      return (
        <ScheduleTemplateMessagePreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "schedule_template_broadcast") {
      return (
        <ScheduleTemplateBroadcastPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          templates={templates}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "compare_date_of_birth") {
      return (
        <CompareDateOfBirthPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          tags={tags}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "compare_tag") {
      return (
        <CompareTagPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          tags={tags}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "delivery_method") {
      return (
        <DeliveryMethodPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "product_discovery") {
      return (
        <ProductDiscoveryPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "leadgen_form") {
      return (
        <LeadgenFormPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "rich_text_message") {
      return (
        <RichTextMessagePreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "interactive_menu") {
      return (
        <InteractiveMenuPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          nodeId={selectedNode?.data?.id}
          nodes={nodes}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "lead_email_notification") {
      return (
        <EmailNotificationPreset
          config={config}
          handleConfigChange={handleConfigChange}
          nodes={nodes}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "lead_whatsapp_notification") {
      return (
        <WhatsAppNotificationPreset
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "shipping_charge_calculation") {
      return (
        <ShippingChargeCalculationPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "payment_link") {
      return (
        <PaymentLinkPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          enabledPaymentGateways={enabledPaymentGateways}
          paymentGatewayDetails={paymentGatewayDetails}
          lastStripeWebhookEvent={lastStripeWebhookEvent}
          tenant={tenant}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "order_confirmation") {
      return (
        <OrderConfirmationPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
          enabledEcommercePlatforms={enabledEcommercePlatforms}
          ecommercePlatformDetails={ecommercePlatformDetails}
        />
      );
    }

    if (selectedNode?.data?.node_type_key === "survey_questions") {
      return (
        <SurveyQuestionsPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      );
    }

    return (
      <>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 1 }}>
          Configuration:
        </Typography>

        {orderedKeys
          .filter((key) => properties[key])
          .map((key) => {
            const fieldSchema = properties[key];
            const required = nodeType.config_schema.required?.includes(key);

            // ---------- ENUM ----------
            // Checked before STRING/NUMBER below: the backend always writes an
            // enum field as e.g. {"type": "string", "enum": [...]}, never
            // {"type": "enum"} — so this must key off the presence of `enum`,
            // not `type`, or every enum field silently renders as free text.
            if (Array.isArray(fieldSchema.enum)) {
              return (
                <EnumField
                  key={key}
                  label={formatTitle(key)}
                  schema={fieldSchema}
                  value={config[key] || ""}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            // ---------- STRING ----------
            if (fieldSchema.type === "string") {
              return (
                <StringField
                  key={key}
                  fieldKey={key}
                  schema={fieldSchema}
                  value={config[key]}
                  required={required}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            // ---------- BOOLEAN ----------
            if (fieldSchema.type === "boolean") {
              return (
                <BooleanField
                  key={key}
                  fieldKey={key}
                  schema={fieldSchema}
                  value={config[key]}
                  required={required}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            // ---------- NUMBER ----------
            if (fieldSchema.type === "number") {
              return (
                <NumberField
                  key={key}
                  fieldKey={key}
                  schema={fieldSchema}
                  value={config[key]}
                  required={required}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            // ---------- ARRAY OF OBJECT ----------
            if (
              fieldSchema.type === "array" &&
              fieldSchema.items?.type === "object"
            ) {
              return (
                <ArrayObjectField
                  key={key}
                  label={formatTitle(key)}
                  schema={fieldSchema}
                  value={config[key] || []}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            return null;
          })}
      </>
    );
  };

  const isOtpNode = selectedNode?.data?.category === "otp";
  // An OTP flow has two End nodes (verified / not verified), so the one-end-node hint doesn't apply
  const hasOtpNodes = (nodes || []).some((node) => node.data?.category === "otp");

  return (
    <Stack
      sx={{
        flex: 0.25,
        borderLeft: "1px solid",
        borderColor: ColorsConstants.primaryGray10,
        height: "80vh",
        overflowY: "auto",
      }}
    >
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        sx={{
          borderBottom: "1px solid",
          borderColor: ColorsConstants.primaryGray10,
          p: 2,
          backgroundColor: "white",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Settings />
        <Typography sx={{ ...typography.h2, fontWeight: 600 }}>
          Node Configuration
        </Typography>
      </Stack>

      <Stack gap={2} sx={{ p: 2 }}>
        {isOtpNode ? (
          <OtpPanelHeader
            title={formatTitle(selectedNode?.data?.name)}
            color={getNodeColor(selectedNode?.data?.category, selectedNode?.data?.node_type_key)}
            whenToUse={selectedNode?.data?.when_to_use}
          />
        ) : (
        <>
        <Box
          sx={{
            border: "1px solid",
            borderColor: ColorsConstants.primaryGray10,
            borderRadius: 2,
            p: 2,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 1 }}>
            {formatTitle(selectedNode?.data?.name)}
          </Typography>
          <Typography
            sx={{
              ...typography.bodyLarge,
              color: getNodeColor(selectedNode?.data?.category, selectedNode?.data?.node_type_key),
              fontWeight: 600,
            }}
          >
            {formatTitle(selectedNode?.data?.category)}
          </Typography>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: ColorsConstants.primaryGray10,
            borderRadius: 2,
            p: 2,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 1 }}>
            What this node does:
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, lineHeight: 1.6 }}>
            {selectedNode?.data?.description}
          </Typography>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: ColorsConstants.primaryGray10,
            borderRadius: 2,
            p: 2,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 1 }}>
            When to Use:
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, lineHeight: 1.6 }}>
            {selectedNode?.data?.when_to_use || "Use as needed"}
          </Typography>
        </Box>
        </>
        )}

        {selectedNode?.data?.category === "start" && (
          <Alert
            severity="info"
            icon={<Info />}
            sx={{ "& .MuiAlert-message": typography.bodyLarge }}
          >
            This is the start node. Every flow must have exactly one start node.
          </Alert>
        )}

        {selectedNode?.data?.category === "end" && !hasOtpNodes && (
          <Alert
            severity="info"
            icon={<Info />}
            sx={{ "& .MuiAlert-message": typography.bodyLarge }}
          >
            This is the end node. Every flow must have exactly one end node.
          </Alert>
        )}

        <Stack gap={2}>
          {renderConfigFields()}

          {error && (
            <Alert
              severity="error"
              sx={{ "& .MuiAlert-message": typography.bodyLarge }}
            >
              {error}
            </Alert>
          )}

          {isDirty && !error && (
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{ "& .MuiAlert-message": typography.bodyLarge }}
            >
              You have unsaved changes — click Save Config or they'll be lost on reload.
            </Alert>
          )}

          <Stack direction="row" gap={2}>
            <ButtonOne
              title={loading ? "Saving..." : "Save Config"}
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave}
              disabled={loading}
              sx={typography.bodyLarge}
            />
            {selectedNode?.data?.category !== "start" && (
              <Button
                variant="contained"
                startIcon={<Delete />}
                onClick={handleDelete}
                disabled={true}
                sx={{
                  ...typography.bodyLarge,
                  backgroundColor: "#d32f2f",
                  "&:hover": { backgroundColor: "#b71c1c" },
                }}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default NodeConfig;
